import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Phone, X, Printer, CheckCircle2, ChevronDown, ChevronUp, PhoneOff, PhoneCall, AlertTriangle } from 'lucide-react';

/**
 * PhoneCallScriptModal — guided phone-qualifier script that doubles as
 * a data-entry form. Every spoken line has an inline field next to it so
 * the staff member captures the answer without leaving the script.
 *
 * Props:
 *   open   : boolean — controls visibility
 *   form   : current Lead form snapshot (read-only — we only patch what changes)
 *   onApply(patch) — apply the captured answers back to the parent form
 *   onClose()
 */
const ROOF_TYPES = [
  { value: '', label: '— Ask client —' },
  { value: 'Iron', label: 'Iron / Long-run' },
  { value: 'Concrete', label: 'Concrete Tile' },
  { value: 'Metal Tile', label: 'Metal Tile' },
  { value: 'Decramastic', label: 'Decramastic' },
  { value: 'Clay', label: 'Clay' },
  { value: 'Other', label: 'Other / Unsure' },
];

// Call-status segmented control — drives short-circuit auto-fills so a
// missed call still leaves a clean audit trail without typing.
const CALL_STATUSES = [
  { key: 'connected', label: 'Talking now',           icon: PhoneCall, accent: 'emerald' },
  { key: 'voicemail', label: 'No answer / Voicemail', icon: PhoneOff,  accent: 'amber'   },
  { key: 'wrong',     label: 'Wrong number',          icon: AlertTriangle, accent: 'rose' },
];

/**
 * Add `days` working days to `from`, skipping Saturday + Sunday.
 * Returns an ISO date string (YYYY-MM-DD) so it slots straight into
 * the <input type="date"> bound to `next_follow_up_date`.
 */
function addWorkingDays(from, days) {
  const d = new Date(from);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();           // 0 Sun .. 6 Sat
    if (dow !== 0 && dow !== 6) added += 1;
  }
  return d.toISOString().slice(0, 10);
}

export default function PhoneCallScriptModal({ open, form, onApply, onClose }) {
  // Mirror of the parent form's bindable fields. Initialised from `form` on
  // open and committed back via onApply when the operator clicks "Save".
  const [draft, setDraft] = useState({});
  const [showOptional, setShowOptional] = useState(false);
  const [callStatus, setCallStatus] = useState('connected');
  const [autoFillFlash, setAutoFillFlash] = useState('');
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setDraft({
      contact_first_name: form?.contact_first_name || '',
      contact_surname:    form?.contact_surname    || '',
      contact_phone:      form?.contact_phone      || '',
      contact_mobile:     form?.contact_mobile     || '',
      contact_email:      form?.contact_email      || '',
      contact_street:     form?.contact_street     || '',
      contact_suburb:     form?.contact_suburb     || '',
      contact_city:       form?.contact_city       || '',
      pre_qualified_roof_type: form?.pre_qualified_roof_type || '',
      pre_qualified_house_age: form?.pre_qualified_house_age || '',
      lead_requirements:  form?.lead_requirements  || '',
      next_follow_up_date: form?.next_follow_up_date || '',
      outcome:            form?.outcome            || '',
      lead_closed:        Boolean(form?.lead_closed),
    });
    setShowOptional(false);
    setCallStatus('connected');
    setAutoFillFlash('');
    // Defer focus until after render
    setTimeout(() => firstInputRef.current?.focus(), 80);
  }, [open, form]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  /**
   * Handle the "Did the call go through?" toggle. Two of the three branches
   * short-circuit the data entry:
   *   - 'voicemail' → outcome "No Answer" + next_follow_up_date = +2 working days
   *   - 'wrong'     → outcome "Wrong Number" + close the lead
   *   - 'connected' → revert outcome/follow-up to whatever the lead already had
   * Auto-fills are reversible: switching back to "Talking now" wipes them.
   */
  const handleCallStatus = (key) => {
    setCallStatus(key);
    setDraft((d) => {
      if (key === 'voicemail') {
        const nextDate = addWorkingDays(new Date(), 2);
        setAutoFillFlash(`Set outcome → "No Answer", next follow-up → ${nextDate} (today + 2 working days)`);
        return { ...d, outcome: 'No Answer', next_follow_up_date: nextDate, lead_closed: false };
      }
      if (key === 'wrong') {
        setAutoFillFlash(`Set outcome → "Wrong Number" and marked the lead as closed`);
        return { ...d, outcome: 'Wrong Number', lead_closed: true };
      }
      // Reset the auto-fills back to the lead's original state when the
      // operator flips back to "Talking now".
      setAutoFillFlash('');
      return {
        ...d,
        outcome: form?.outcome || '',
        next_follow_up_date: form?.next_follow_up_date || '',
        lead_closed: Boolean(form?.lead_closed),
      };
    });
  };

  const fullName = useMemo(
    () => `${(draft.contact_first_name || '').trim()} ${(draft.contact_surname || '').trim()}`.trim(),
    [draft.contact_first_name, draft.contact_surname]
  );

  const handleApply = () => {
    if (typeof onApply === 'function') onApply(draft);
    onClose?.();
  };

  // Print-friendly view — opens a new window with just the script (no
  // inputs, just bold prompts) so staff can keep a paper copy at the desk.
  const handlePrint = () => {
    const win = window.open('', '_blank', 'noopener,noreferrer,width=800,height=900');
    if (!win) return;
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>RTR Lead Call Script</title>
      <style>
        body { font-family: Georgia, serif; max-width: 720px; margin: 24px auto; padding: 0 24px; color: #0f172a; line-height: 1.55; }
        h1 { color: #D35400; font-size: 22px; margin-bottom: 4px; }
        h2 { color: #334155; font-size: 14px; text-transform: uppercase; letter-spacing: 0.12em; margin-top: 22px; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; }
        p.line { margin: 8px 0; }
        .label { color: #64748B; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; }
        .field { border-bottom: 1px solid #94A3B8; min-height: 22px; padding: 4px 0; margin: 2px 0 12px; }
        .row { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        em { color: #94A3B8; }
      </style></head><body>
      <h1>RTR — Lead Phone Qualifier Script</h1>
      <p><em>Use this script the first time you speak to a new lead. Tick off each step as you go.</em></p>

      <h2>1. Greeting</h2>
      <p class="line">"Hi, this is <strong>[your name]</strong> from <strong>Roofs To Remedy</strong>. Am I speaking with…?"</p>
      <div class="row"><div><div class="label">First name</div><div class="field"></div></div>
        <div><div class="label">Surname</div><div class="field"></div></div></div>

      <h2>2. Best contact details</h2>
      <p class="line">"Just to make sure I've got the right details — what's the best phone number to reach you on?"</p>
      <div class="row"><div><div class="label">Phone</div><div class="field"></div></div>
        <div><div class="label">Mobile</div><div class="field"></div></div></div>
      <p class="line">"And an email address for sending the written quotation?"</p>
      <div class="label">Email</div><div class="field"></div>

      <h2>3. Property address</h2>
      <p class="line">"What's the property address — street, suburb and city?"</p>
      <div class="label">Address</div><div class="field"></div>
      <div class="row"><div><div class="label">Suburb</div><div class="field"></div></div>
        <div><div class="label">City</div><div class="field"></div></div></div>

      <h2>4. Roof type pre-qualification</h2>
      <p class="line">"What type of roof do you have — is it Iron / long-run, Concrete tile, Metal tile, Decramastic or Clay?"</p>
      <div class="label">Roof type</div><div class="field"></div>

      <h2>5. House age</h2>
      <p class="line">"Roughly how old is the house? Even an estimate helps — built in the 70s? 90s? Recently?"</p>
      <div class="label">House age (approx.)</div><div class="field"></div>

      <h2>6. What they're after</h2>
      <p class="line">"What's prompted the call — is it a leak, a routine appraisal, a full re-roof quote, or something else?"</p>
      <div class="label">Requirements / notes</div><div class="field" style="min-height:60px"></div>

      <h2>7. Next step</h2>
      <p class="line">"Great — I'll get one of our inspectors out for a free appraisal. When suits you?"</p>
      <div class="label">Next follow-up / inspection date</div><div class="field"></div>

      <h2>8. Close</h2>
      <p class="line">"Thanks for calling Roofs To Remedy — we'll see you on the day."</p>
      <p style="margin-top:32px;font-size:11px;color:#94A3B8">Generated ${new Date().toLocaleString('en-NZ')}</p>
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 250);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="phone-script-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-[#D35400]/10 to-transparent">
          <div className="flex items-center gap-2">
            <Phone size={18} className="text-[#D35400]" />
            <div>
              <h2 className="text-base font-bold text-slate-900">Phone Qualifier Script</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">Speak each line, type the answer — saves back to the lead in one click.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="phone-script-print"
              onClick={handlePrint}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded transition-colors"
            >
              <Printer size={14} /> Print
            </button>
            <button onClick={onClose} className="p-1 text-slate-500 hover:text-slate-700"><X size={18} /></button>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {/* Call-status toggle — sits at the top of every call so the
              operator can short-circuit data entry on a missed call. */}
          <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="phone-script-call-status">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Did the call go through?</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Toggle a missed call → auto-fills the outcome + next follow-up so no lead slips through the cracks.</p>
              </div>
              <div className="inline-flex bg-slate-100 rounded-md p-0.5">
                {CALL_STATUSES.map((s) => {
                  const Icon = s.icon;
                  const active = callStatus === s.key;
                  const colour = active
                    ? (s.accent === 'emerald' ? 'bg-emerald-600 text-white' :
                       s.accent === 'amber'   ? 'bg-amber-500 text-white'  :
                                                'bg-rose-600 text-white')
                    : 'text-slate-600 hover:bg-white';
                  return (
                    <button
                      key={s.key}
                      type="button"
                      data-testid={`phone-script-status-${s.key}`}
                      onClick={() => handleCallStatus(s.key)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded transition-colors ${colour}`}
                    >
                      <Icon size={12} /> {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {autoFillFlash && (
              <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 px-2 py-1 rounded flex items-center gap-1" data-testid="phone-script-autofill-flash">
                <CheckCircle2 size={11} /> {autoFillFlash}
              </div>
            )}
          </div>

          {/* For a voicemail / wrong-number call, the rest of the script
              isn't applicable — just collect a quick note + save. */}
          {callStatus !== 'connected' && (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded p-3 space-y-2" data-testid="phone-script-shortcircuit">
              <p className="text-sm text-slate-700">
                {callStatus === 'voicemail'
                  ? <>No answer or voicemail — leave a brief note about your attempt. The follow-up reminder is already set for 2 working days from today.</>
                  : <>Marked as wrong number — the lead has been closed. Add a quick note if you want context.</>}
              </p>
              <textarea
                data-testid="ps-shortcircuit-note"
                value={draft.lead_requirements}
                onChange={(e) => set('lead_requirements', e.target.value)}
                rows={2}
                placeholder={callStatus === 'voicemail'
                  ? "e.g. Left voicemail at 10:43 — said we'd ring back Wed"
                  : "e.g. Recipient says they never enquired"}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white focus:border-[#D35400] focus:outline-none"
              />
            </div>
          )}

          {callStatus === 'connected' && (<>
          {/* 1. Greeting */}
          <ScriptLine
            step={1}
            spoken={
              <>Hi, this is <em>[your name]</em> from <strong>Roofs To Remedy</strong>. Am I speaking with…?</>
            }
          >
            <div className="grid grid-cols-2 gap-2">
              <Inp ref={firstInputRef} label="First name" testid="ps-first-name" value={draft.contact_first_name} onChange={(v) => set('contact_first_name', v)} />
              <Inp label="Surname" testid="ps-surname" value={draft.contact_surname} onChange={(v) => set('contact_surname', v)} />
            </div>
            {fullName && <div className="text-[11px] text-emerald-700 font-semibold mt-1 flex items-center gap-1"><CheckCircle2 size={11} /> Speaking with <span className="font-mono-num">{fullName}</span></div>}
          </ScriptLine>

          {/* 4. Roof type — PROMOTED to top after greeting because it's the
              key piece of information that gates everything else. */}
          <ScriptLine
            step={2}
            highlight
            spoken={
              <>What <strong>type of roof</strong> do you have — is it <strong>Iron</strong> (long-run), <strong>Concrete tile</strong>, <strong>Metal tile</strong>, <strong>Decramastic</strong> or <strong>Clay</strong>?</>
            }
          >
            <select
              data-testid="ps-roof-type"
              value={draft.pre_qualified_roof_type}
              onChange={(e) => set('pre_qualified_roof_type', e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white focus:border-[#D35400] focus:outline-none"
            >
              {ROOF_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </ScriptLine>

          {/* 5. House age */}
          <ScriptLine
            step={3}
            highlight
            spoken={
              <>Roughly how <strong>old is the house</strong>? Even an estimate helps — built in the 70s? 90s? Recently?</>
            }
          >
            <Inp testid="ps-house-age" value={draft.pre_qualified_house_age} onChange={(v) => set('pre_qualified_house_age', v)} placeholder="e.g. Built 1972, ~30 yrs, brand new build" />
          </ScriptLine>

          {/* 6. Requirements */}
          <ScriptLine
            step={4}
            spoken={
              <>What's prompted the call — is it a <strong>leak</strong>, a routine <strong>appraisal</strong>, a <strong>full re-roof quote</strong>, or something else?</>
            }
          >
            <textarea
              data-testid="ps-requirements"
              value={draft.lead_requirements}
              onChange={(e) => set('lead_requirements', e.target.value)}
              rows={2}
              placeholder="Free-text notes — leak in laundry, broken tile, re-coat enquiry…"
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white focus:border-[#D35400] focus:outline-none"
            />
          </ScriptLine>

          {/* 7. Next follow-up */}
          <ScriptLine
            step={5}
            spoken={
              <>Great — I'll get one of our inspectors out for a <strong>free appraisal</strong>. When suits you?</>
            }
          >
            <Inp type="date" testid="ps-next-followup" value={draft.next_follow_up_date} onChange={(v) => set('next_follow_up_date', v)} />
          </ScriptLine>

          {/* Optional — collapsed by default to keep the script short. Reopens
              the rest of the fields when the staff member needs them. */}
          <button
            type="button"
            data-testid="phone-script-show-optional"
            onClick={() => setShowOptional((v) => !v)}
            className="w-full text-xs font-semibold text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1 py-2 border-t border-dashed border-slate-200"
          >
            {showOptional ? <><ChevronUp size={12} /> Hide optional fields</> : <><ChevronDown size={12} /> Show optional fields (phone / address / email)</>}
          </button>
          {showOptional && (
            <div className="space-y-3 pt-1">
              <ScriptLine step={6} compact spoken={<>What's the best <strong>phone number</strong> + an <strong>email</strong> for sending the written quote?</>}>
                <div className="grid grid-cols-2 gap-2">
                  <Inp label="Phone"  testid="ps-phone"  value={draft.contact_phone}  onChange={(v) => set('contact_phone', v)} />
                  <Inp label="Mobile" testid="ps-mobile" value={draft.contact_mobile} onChange={(v) => set('contact_mobile', v)} />
                </div>
                <Inp label="Email" testid="ps-email" value={draft.contact_email} onChange={(v) => set('contact_email', v)} />
              </ScriptLine>
              <ScriptLine step={7} compact spoken={<>And the <strong>property address</strong> — street, suburb, city?</>}>
                <Inp label="Street" testid="ps-street" value={draft.contact_street} onChange={(v) => set('contact_street', v)} />
                <div className="grid grid-cols-2 gap-2">
                  <Inp label="Suburb" testid="ps-suburb" value={draft.contact_suburb} onChange={(v) => set('contact_suburb', v)} />
                  <Inp label="City"   testid="ps-city"   value={draft.contact_city}   onChange={(v) => set('contact_city', v)} />
                </div>
              </ScriptLine>
            </div>
          )}
          </>)}
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <p className="text-[11px] text-slate-500 italic">Tip: Press Enter to move between fields. Hit "Save to lead" to apply everything.</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button
              data-testid="phone-script-apply"
              onClick={handleApply}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-[#D35400] hover:bg-[#A04000] rounded transition-colors"
            >
              <CheckCircle2 size={14} /> Save to lead
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}


function ScriptLine({ step, spoken, children, highlight, compact }) {
  return (
    <div
      className={`rounded-md ${compact ? 'p-2' : 'p-3'} ${highlight ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 border border-slate-200'}`}
      data-testid={`phone-script-step-${step}`}
    >
      <div className="flex items-start gap-2">
        <span className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${highlight ? 'bg-amber-500 text-white' : 'bg-slate-300 text-slate-700'}`}>{step}</span>
        <p className="text-sm text-slate-700 leading-snug">
          <span className="text-slate-400 italic mr-1">"</span>
          {spoken}
          <span className="text-slate-400 italic ml-1">"</span>
        </p>
      </div>
      <div className="mt-2 ml-7 space-y-2">{children}</div>
    </div>
  );
}


const Inp = React.forwardRef(function Inp({ label, value, onChange, placeholder = '', type = 'text', testid }, ref) {
  return (
    <label className="block">
      {label && <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">{label}</span>}
      <input
        ref={ref}
        type={type}
        data-testid={testid}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white focus:border-[#D35400] focus:outline-none"
      />
    </label>
  );
});
